import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// Phase durations (ms). Full cycle ≈ 7.5s.
const D = { original: 1000, erase: 600, enter: 260, write: 1100, down: 360, tap: 380, exit: 320, hold: 3000, fade: 450 };
const ORDER = ["original", "erase", "enter", "write", "down", "tap", "exit", "hold", "fade"];
const EASE = [0.22, 1, 0.36, 1];
// Padding (cancelled by negative margin) so clip insets never cut glyph overhangs.
const PILL_CLS = "relative z-10 inline-flex items-center whitespace-nowrap pl-2 pr-[calc(0.5rem-0.2em)] py-[3px] rounded-md bg-[#EA580C] text-white text-[11.5px] uppercase tracking-[0.2em] font-semibold leading-none";
const CLIP_PAD = { padding: "0.2em 0", margin: "-0.2em 0" };
// Paint-only text reveal (narrow cards): glyphs are painted by a background
// whose size animates, so no clip/compositor layer ever touches the backdrop.
const paintStyle = (color, anchor) => ({
  color: "transparent", WebkitTextFillColor: "transparent",
  backgroundImage: `linear-gradient(${color}, ${color})`, backgroundRepeat: "no-repeat",
  backgroundPosition: `${anchor} center`, WebkitBackgroundClip: "text", backgroundClip: "text",
});

// One shared clock drives every mounted instance so hero + card loops stay in step.
const subs = new Set();
let clockIdx = -1;
let clockTimer = null;
function clockTick() {
  clockIdx = (clockIdx + 1) % ORDER.length;
  const p = ORDER[clockIdx];
  subs.forEach((fn) => fn(p));
  clockTimer = setTimeout(clockTick, D[p]);
}
function clockStart() { clearTimeout(clockTimer); clockIdx = -1; clockTick(); }
function clockStop() { clearTimeout(clockTimer); clockTimer = null; }
function onVisibility() {
  if (document.hidden) { clockStop(); subs.forEach((fn) => fn("hold")); }
  else if (subs.size) clockStart();
}
function subscribe(fn) {
  subs.add(fn);
  if (subs.size === 1) { document.addEventListener("visibilitychange", onVisibility); clockStart(); }
  else fn(clockTimer ? ORDER[clockIdx] : "hold");
  return () => {
    subs.delete(fn);
    if (!subs.size) { clockStop(); document.removeEventListener("visibilitychange", onVisibility); }
  };
}

// Diwali fireworks: gentle looping sparkle for the whole hold — repeating
// spark-trail bursts, twinkling stars and a soft glow pulse. `ix`/`iy` is an
// exclusion ellipse (half-size of the framed content) sparks start outside of;
// cards use 0 (burst from the pill centre), the hero frames price + pill.
const SPARK_BASE = Array.from({ length: 12 }, (_, i) => ({
  a: (i / 12) * 360 + (i % 2 ? 6 : -4), r: 40 + (i % 3) * 8, d: (i % 4) * 0.05 + (i < 6 ? 0 : 0.55),
  c: i % 3 === 0 ? "#F59E0B" : i % 3 === 1 ? "#FCD34D" : "#FDE68A",
}));
const STAR_BASE = Array.from({ length: 10 }, (_, i) => ({
  a: ((i / 10) * 360 + 15) * (Math.PI / 180), r: 26 + (i % 3) * 11, d: (i % 5) * 0.18, s: i % 2 ? 2.5 : 3.5,
  c: i % 3 === 0 ? "#EA580C" : i % 3 === 1 ? "#FBBF24" : "#FDE68A",
}));
const BURST_T = { duration: 1.1, ease: [0.16, 1, 0.3, 1], repeat: Infinity, repeatDelay: 0.35 };
const ellipseR = (ix, iy, deg) => {
  if (!ix) return 0;
  const t = (deg * Math.PI) / 180;
  return (ix * iy) / Math.sqrt((iy * Math.cos(t)) ** 2 + (ix * Math.sin(t)) ** 2);
};

function Fireworks({ testid, dimmed, fadeMs, ix = 0, iy = 0, k = 1, z = 0, sy = 1 }) {
  const sparks = SPARK_BASE.map((b) => ({ ...b, o: ellipseR(ix, iy, b.a), r: b.r * k }));
  const stars = STAR_BASE.map((b) => ({ ...b,
    x: Math.cos(b.a) * (b.r * 1.35 * k + ix * 1.12), y: Math.sin(b.a) * (b.r * 0.75 * k + iy * 1.25) }));
  return (
    <motion.span aria-hidden="true" data-testid={testid}
      className="absolute left-1/2 top-1/2 pointer-events-none" style={{ width: 0, height: 0, zIndex: z, scaleY: sy }}
      initial={{ opacity: 0 }} animate={{ opacity: dimmed ? 0 : 1 }}
      transition={{ duration: dimmed ? fadeMs / 1000 : 0.25, ease: "easeInOut" }}>
      <motion.span className="absolute rounded-full"
        style={{ width: 28 + ix * 0.9, height: 28 + iy * 0.6, left: -(28 + ix * 0.9) / 2, top: -(28 + iy * 0.6) / 2,
                 background: "radial-gradient(ellipse, rgba(253,230,138,0.5) 0%, rgba(245,158,11,0.16) 45%, rgba(245,158,11,0) 70%)" }}
        initial={{ scale: 0.3, opacity: 0.8 }} animate={{ scale: [0.3, 3.2, 3.6], opacity: [0.8, 0.25, 0] }}
        transition={{ duration: 1.3, ease: "easeOut", repeat: Infinity, repeatDelay: 0.15 }} />
      {sparks.map((s, i) => (
        <span key={`s${i}`} className="absolute left-0 top-0" style={{ transform: `rotate(${s.a}deg)` }}>
          <motion.span className="absolute rounded-full"
            style={{ height: 2, top: -1, left: 0, transformOrigin: "0 50%",
                     background: `linear-gradient(90deg, rgba(253,230,138,0) 0%, ${s.c} 100%)` }}
            initial={{ width: 0, x: s.o, opacity: 0 }}
            animate={{ width: [0, s.r * 0.5, s.r * 0.25, 0], x: [s.o, s.o + s.r * 0.45, s.o + s.r * 0.8, s.o + s.r * 0.9], opacity: [0, 0.9, 0, 0] }}
            transition={{ ...BURST_T, delay: s.d }} />
          <motion.span className="absolute rounded-full"
            style={{ width: 3.5, height: 3.5, top: -1.75, left: 0, background: s.c, boxShadow: `0 0 8px ${s.c}` }}
            initial={{ x: s.o, opacity: 0, scale: 0.6 }}
            animate={{ x: [s.o, s.o + s.r * 0.85, s.o + s.r, s.o + s.r * 1.02], opacity: [0, 1, 0.2, 0], scale: [0.6, 1, 0.5, 0.3] }}
            transition={{ ...BURST_T, delay: s.d }} />
        </span>
      ))}
      {stars.map((t, i) => (
        <motion.span key={`t${i}`} className="absolute rounded-full"
          style={{ width: t.s, height: t.s, left: t.x - t.s / 2, top: t.y - t.s / 2, background: t.c, boxShadow: `0 0 6px ${t.c}` }}
          initial={{ opacity: 0, scale: 0.4 }}
          animate={{ opacity: [0, 1, 0], scale: [0.4, 1.3, 0.4] }}
          transition={{ duration: 0.9, ease: "easeInOut", repeat: Infinity, repeatDelay: 0.3, delay: t.d }} />
      ))}
    </motion.span>
  );
}

const VARIANTS = {
  hero: {
    root: "flex flex-col items-center gap-1.5",
    price: "font-display text-[20px] sm:text-[22px] font-semibold leading-none tracking-tight",
    per: "ml-1.5 text-[12px] sm:text-[13px] font-medium text-white/60 tracking-wide",
    pillWrap: "relative inline-flex",
    handH: () => (window.innerWidth >= 640 ? 72 : 62),
    eraser: { w: 46, h: 20 },
    ids: { root: "hero-pricing", original: "hero-pricing-original", final: "hero-membership-price",
           eraser: "hero-price-eraser", label: "hero-pricing-label", ripple: "hero-price-ripple", hand: "hero-price-hand",
           fireworks: "hero-price-fireworks" },
  },
  card: {
    root: "flex flex-wrap items-baseline gap-x-2.5 gap-y-2",
    price: "font-display text-[42px] md:text-[50px] font-bold leading-none tracking-tightest",
    per: "ml-2.5 text-base md:text-[17px] font-normal text-white/60 tracking-normal",
    pillWrap: "relative inline-flex self-center",
    handH: () => (window.innerWidth >= 768 ? 96 : 84),
    eraser: { w: 72, h: 30 },
    ids: null,
  },
};

/** Continuous price-reveal loop: ₹2,999 → eraser → hand slides L→R while
 *  ₹1,999 per year is revealed in white ink → pen taps → ripple → DIWALI OFFER
 *  pops in → hold 2s → fade → repeat. Visual only; the charged amount lives in
 *  the backend. Pauses while the tab is hidden; reduced motion = static offer.
 *  `variant` = "hero" (stacked, small) | "card" (large price, label inline). */
export function PriceReveal({ variant = "hero", testid = "pricing" }) {
  const V = VARIANTS[variant];
  const ids = V.ids || { root: testid, original: `${testid}-original`, final: `${testid}-amount`, eraser: `${testid}-eraser`,
                         label: `${testid}-label`, ripple: `${testid}-ripple`, hand: `${testid}-hand`, fireworks: `${testid}-fireworks` };
  const rootRef = useRef(null);
  const priceRef = useRef(null);
  const origRef = useRef(null);
  const pillRef = useRef(null);
  const [phase, setPhase] = useState("original");
  const [geom, setGeom] = useState(null);
  const [reduced] = useState(() =>
    typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches
  );
  // Narrow card: "per year" + pill stack in a column right of the amount so the
  // offer never wraps under the price. Hero is unaffected.
  const [narrow, setNarrow] = useState(() => typeof window !== "undefined" && window.innerWidth < 640);
  useEffect(() => {
    if (variant !== "card") return undefined;
    const onResize = () => setNarrow(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [variant]);
  const stacked = variant === "card" && narrow;

  useEffect(() => {
    if (reduced) { setPhase("hold"); return undefined; }
    const measure = () => {
      const root = rootRef.current?.getBoundingClientRect();
      const price = priceRef.current?.getBoundingClientRect();
      const pill = pillRef.current?.getBoundingClientRect();
      if (!root || !price || !pill) return;
      setGeom({
        textLeft: price.left - root.left, textRight: price.right - root.left,
        textW: price.width, textTop: price.top - root.top, textBottom: price.bottom - root.top,
        // Layout widths (transform-free): ₹2,999 is wider than ₹1,999 in this face.
        origW: origRef.current?.offsetWidth || price.width,
        extraW: Math.max(0, (origRef.current?.offsetWidth || 0) - (priceRef.current?.offsetWidth || 0)),
        pillCx: pill.left - root.left + pill.width / 2, pillCy: pill.top - root.top + pill.height / 2,
        groupW: Math.max(price.right, pill.right) - Math.min(price.left, pill.left),
        groupH: Math.max(price.bottom, pill.bottom) - Math.min(price.top, pill.top),
        handH: V.handH(),
      });
    };
    return subscribe((p) => { measure(); setPhase(p); });
  }, [reduced, V, stacked]);

  // Reduced motion: the same offer, fully static — regular price struck
  // through, offer price + pill in the identical typography. No clock runs.
  if (reduced) {
    const oldCls = stacked ? "text-white/45 line-through decoration-white/40 mr-2"
      : variant === "hero" ? "text-[13px] sm:text-[14px] font-medium text-white/45 line-through decoration-white/40 tracking-wide mr-2"
      : "text-[17px] md:text-[19px] font-medium text-white/45 line-through decoration-white/40 mr-2.5";
    const pillEl = (
      <span className="relative inline-flex">
        <span data-testid={ids.label} className={PILL_CLS}>Diwali Offer</span>
      </span>
    );
    return (
      <div ref={rootRef} data-testid={ids.root} data-phase="hold" data-reduced="1" data-stacked={stacked ? "1" : "0"}
           className={`relative z-10 ${stacked ? "flex flex-wrap items-start gap-x-2.5 gap-y-2" : V.root}`}>
        <span data-testid={ids.final} className={`block whitespace-nowrap text-white ${V.price}`}>
          {!stacked && <s className={oldCls}>₹2,999</s>}
          ₹1,999{!stacked && <span className={V.per}>per year</span>}
        </span>
        {stacked ? (
          <div className="flex flex-col items-start justify-between self-stretch shrink-0">
            <span className={`${V.per} leading-none`} style={{ marginLeft: 0 }}>
              <s className={oldCls}>₹2,999</s>per year
            </span>
            {pillEl}
          </div>
        ) : pillEl}
      </div>
    );
  }

  const g = geom;
  const showOriginal = phase === "original" || phase === "erase";
  const finalWritten = ["write", "down", "tap", "exit", "hold", "fade"].includes(phase);
  const pillShown = ["exit", "hold", "fade"].includes(phase) || reduced;
  const fireworksOn = !reduced && (phase === "hold" || phase === "fade");
  const dimmed = phase === "fade";
  const handActive = ["enter", "write", "down", "tap", "exit"].includes(phase);

  // Pen-tip target (root coords); the image's bottom-left corner is the tip.
  // While idle the hand is parked (invisible) at the writing start so the
  // first visible frame is already complete and correctly placed.
  const tip = (() => {
    if (!g) return { x: 0, y: 0 };
    if (phase === "write") return { x: g.textRight, y: g.textBottom };
    if (phase === "down") return { x: g.pillCx, y: g.pillCy };
    if (phase === "tap") return { x: g.pillCx, y: g.pillCy + 3 };
    if (phase === "exit") return { x: g.pillCx + 40, y: g.pillCy + 36 };
    return { x: g.textLeft, y: g.textBottom - (phase === "enter" ? 0 : 6) };
  })();
  const handAnim = g ? { x: tip.x, y: tip.y - g.handH, opacity: handActive && phase !== "exit" ? 1 : 0 } : {};
  const handTrans = phase === "enter" ? { duration: D.enter / 1000, ease: EASE, opacity: { duration: 0 } }
    : phase === "write" ? { duration: D.write / 1000, ease: "linear" }
    : phase === "down" ? { duration: D.down / 1000, ease: EASE }
    : phase === "tap" ? { duration: 0.09, ease: "easeOut" }
    : phase === "exit" ? { duration: D.exit / 1000, ease: EASE }
    : { duration: 0 };

  // Identical typography for both prices — only colour differs.
  const priceCls = `block whitespace-nowrap ${V.price}`;
  const perYear = stacked ? null : <span className={V.per}>per year</span>;
  const E = V.eraser;
  const perShown = showOriginal || ["down", "tap", "exit", "hold"].includes(phase) || reduced;
  const perTrans = phase === "erase" ? { duration: D.erase / 1000, ease: "linear" }
    : phase === "fade" ? { duration: D.fade / 1000, ease: "easeInOut" }
    : phase === "original" ? { duration: 0.35, ease: EASE }
    : phase === "down" ? { duration: 0.25, ease: "easeOut" }
    : { duration: 0 };
  const rootCls = stacked ? "flex flex-wrap items-start gap-x-2.5 gap-y-2" : V.root;
  // The reveal technique not in use is reset instantly (matters when crossing the breakpoint).
  const instantOther = stacked ? { clipPath: { duration: 0 } } : { backgroundSize: { duration: 0 } };
  const pillWrapCls = stacked ? "relative inline-flex" : V.pillWrap;

  const pill = (
      <span ref={pillRef} className={pillWrapCls}>
        <motion.span
          data-testid={ids.label}
          className={PILL_CLS}
          initial={false}
          animate={{ scale: pillShown ? 1 : 0, opacity: pillShown ? (dimmed ? 0 : 1) : 0 }}
          transition={phase === "exit"
            ? { scale: { type: "spring", stiffness: 420, damping: 26, mass: 0.7 }, opacity: { duration: 0.12 } }
            : phase === "fade"
              ? { opacity: { duration: D.fade / 1000, ease: "easeInOut" }, scale: { duration: 0 } }
              : { duration: 0 }}
          style={{ visibility: pillShown ? "visible" : "hidden" }}
        >
          Diwali Offer
        </motion.span>
        {fireworksOn && variant !== "hero" && !stacked && <Fireworks testid={ids.fireworks} dimmed={dimmed} fadeMs={D.fade} />}
        <AnimatePresence>
          {(phase === "tap" || phase === "exit") && [0, 0.12].map((delay, i) => (
            <motion.span
              key={i}
              aria-hidden="true"
              data-testid={`${ids.ripple}-${i}`}
              className="absolute left-1/2 top-1/2 rounded-full border border-white/70 pointer-events-none"
              style={{ width: 14, height: 14, marginLeft: -7, marginTop: -7, zIndex: 1 }}
              initial={{ scale: 0.2, opacity: 0.85 }}
              animate={{ scale: 4.2, opacity: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.62, ease: "easeOut", delay }}
            />
          ))}
        </AnimatePresence>
      </span>
  );

  return (
    <div ref={rootRef} data-testid={ids.root} data-phase={phase} data-stacked={stacked ? "1" : "0"}
         className={`relative z-10 ${rootCls}`} style={{ overflow: "visible" }}>
      <div className="relative shrink-0" style={{ overflow: "visible" }}>
        {/* Original price — visual only; zooms in, then erased L→R. The clip
            lives on a padded inner span with positive-only insets (Safari
            rejects negative / overlapping inset() values). */}
        <motion.span
          ref={origRef}
          aria-hidden="true"
          data-testid={ids.original}
          className={`${priceCls} absolute left-0 top-0 text-white/85`}
          initial={false}
          animate={{ opacity: showOriginal ? 1 : 0, scale: showOriginal ? 1 : 0.9 }}
          transition={phase === "original"
            ? { opacity: { duration: 0.35, ease: EASE }, scale: { duration: 0.35, ease: EASE } }
            : { duration: 0 }}
        >
          <motion.span
            className="inline-block"
            style={stacked ? paintStyle("rgba(255,255,255,0.85)", "right") : CLIP_PAD}
            initial={false}
            animate={stacked
              ? { backgroundSize: phase === "erase" ? "0% 100%" : "100% 100%", clipPath: "none" }
              : { clipPath: phase === "erase" ? "inset(0% 0% 0% 100%)" : "inset(0% 0% 0% 0%)", backgroundSize: "auto" }}
            transition={{ ...(phase === "erase" ? { duration: D.erase / 1000, ease: "linear" } : { duration: 0 }), ...instantOther }}
          >
            ₹2,999{perYear}
          </motion.span>
        </motion.span>

        {/* Offer price — always in the DOM (clip only) so it is announced once */}
        <motion.span
          ref={priceRef}
          data-testid={ids.final}
          className={`${priceCls} text-white`}
          initial={false}
          animate={{ opacity: dimmed ? 0 : 1 }}
          transition={phase === "fade" ? { duration: D.fade / 1000, ease: "easeInOut" } : { duration: 0 }}
        >
          <motion.span
            className="inline-block"
            style={stacked ? paintStyle("#fff", "left") : CLIP_PAD}
            initial={false}
            animate={stacked
              ? { backgroundSize: finalWritten || reduced ? "100% 100%" : "0% 100%", clipPath: "none" }
              : { clipPath: finalWritten || reduced ? "inset(0% 0% 0% 0%)" : "inset(0% 100% 0% 0%)", backgroundSize: "auto" }}
            transition={{ ...(phase === "write" ? { duration: D.write / 1000, ease: "linear" } : { duration: 0 }), ...instantOther }}
          >
            ₹1,999{perYear}
          </motion.span>
        </motion.span>

        {/* Eraser — rides the erase edge, confined to the price line */}
        {phase === "erase" && g && (
          <motion.svg
            aria-hidden="true"
            data-testid={ids.eraser}
            width={E.w} height={E.h} viewBox="0 0 46 20"
            className="absolute pointer-events-none"
            style={{ left: 0, top: "50%", marginTop: -E.h / 2, zIndex: 2 }}
            initial={{ x: -E.w / 2, rotate: -10, opacity: 0 }}
            animate={{ x: (stacked ? g.origW : g.textW) - E.w / 2, rotate: [-10, -6, -12, -8, -10], opacity: [0, 1, 1, 1, 0.9] }}
            transition={{ duration: D.erase / 1000, ease: "linear" }}
          >
            <rect x="1" y="1" width="44" height="18" rx="4" fill="#F6F6F4" stroke="#C9C9C6" strokeWidth="1" />
            <rect x="1" y="13" width="44" height="6" rx="3" fill="#DEDEDA" opacity="0.8" />
            <rect x="26" y="1" width="19" height="18" rx="4" fill="#E9E9E6" opacity="0.7" />
          </motion.svg>
        )}
      </div>

      {/* Offer pill — space always reserved; pops in from the tap point.
          Narrow cards: "per year" sits above it in a column beside the amount. */}
      {stacked ? (
        // While ₹2,999 (wider) is shown the column steps right by the width
        // difference; the step happens only when "per year" and the pill are
        // invisible (start of "original" / "enter"), so nothing visibly jumps.
        <div className="flex flex-col items-start justify-between self-stretch shrink-0"
             style={{ marginLeft: showOriginal && g ? g.extraW : 0 }}>
          <motion.span className={`${V.per} leading-none`} style={{ marginLeft: 0 }} initial={false}
                       animate={{ opacity: perShown && !dimmed ? 1 : 0 }} transition={perTrans}>
            per year
          </motion.span>
          {pill}
        </div>
      ) : pill}

      {fireworksOn && variant === "hero" && g && (
        <Fireworks testid={ids.fireworks} dimmed={dimmed} fadeMs={D.fade} z={-1}
                   ix={g.groupW / 2 + 10} iy={g.groupH / 2 + 12} k={0.45} sy={0.75} />
      )}
      {fireworksOn && stacked && g && (
        <Fireworks testid={ids.fireworks} dimmed={dimmed} fadeMs={D.fade} z={-1}
                   ix={g.groupW / 2 + 2} iy={g.groupH / 2 / 0.36 + 4} k={0.35} sy={0.36} />
      )}

      {/* Hand + pen — always mounted (decoded once, no late first frame); root
          coords; the image's bottom-left corner is the pen tip. Above the CTA. */}
      {!reduced && (
        <motion.img
          aria-hidden="true"
          data-testid={ids.hand}
          data-active={handActive ? "1" : "0"}
          src="/assets/hand-pen.png"
          alt=""
          draggable={false}
          decoding="sync"
          className="absolute pointer-events-none select-none w-auto max-w-none"
          style={{ left: 0, top: 0, height: g ? g.handH : 72, zIndex: 20,
                   visibility: handActive ? "visible" : "hidden",
                   WebkitMaskImage: "linear-gradient(to top right, #000 84%, rgba(0,0,0,0) 99%)",
                   maskImage: "linear-gradient(to top right, #000 84%, rgba(0,0,0,0) 99%)" }}
          initial={false}
          animate={handAnim}
          transition={handTrans}
        />
      )}
    </div>
  );
}
